import fetchAPI from 'utils/fetchAPI';
import moment from 'moment';
import { handleReponse } from 'actions';

const monthLabels = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const sortMonths = obj =>
  Object.keys(obj).sort(
    (x, y) => (obj[x] === obj[y] ? x - y : obj[x] - obj[y])
  );

const sortUserValue = userValue =>
  Object.keys(userValue)
    .filter(
      userId =>
        [
          '449',
          '17',
          '62',
          '21',
          '418',
          '33',
          '5',
          '632',
          '27',
          '31',
          '41',
          '13',
        ].indexOf(userId) < 0
    )
    .map(userId => [userId, userValue[userId]])
    .sort((a, b) => b[1] - a[1])
    .filter(e => e[1] > 0);

const getFormTableData = data => {
  const res = [];
  const sortedMonths = sortMonths(data.month_year);
  res.push(['Form Type']);
  for (let i = 0; i < sortedMonths.length; i++) {
    const m = sortedMonths[i];
    res[0].push(`${monthLabels[m - 1]} ${data.month_year[m]}, $`);
  }

  return [
    ...res,
    ...Object.keys(data.form_value)
      .filter(each => data.form_value.hasOwnProperty(each))
      .map(each => {
        const row = [];
        row.push(each);
        for (let i = 0; i < sortedMonths.length; i++) {
          const m = sortedMonths[i];
          const amount = data.form_value[each][m];
          row.push(amount.toFixed().replace(/(\d)(?=(\d{3})+$)/g, '$1,'));
        }
        return row;
      }),
  ];
};

const getChartData = data => {
  const res = [];
  const sortedMonths = sortMonths(data.month_year);

  for (let i = 0; i < sortedMonths.length; i++) {
    const m = sortedMonths[i];
    const month = Object.assign(
      {},
      { month: `${monthLabels[m - 1]} ${data.month_year[m]}` },
      ...Object.keys(data.monthly_data[m])
        .filter(each => data.monthly_data[m].hasOwnProperty(each))
        .map(each => ({ [each]: data.monthly_data[m][each] }))
    );
    res.push(month);
  }
  return res;
};

const getChartLabel = data =>
  Object.keys(data.total_data)
    .filter(t => data.total_data.hasOwnProperty(t))
    .map((t, idx) => ({
      balloonText: '[[title]]:<b>[[value]]</b>',
      fillAlphas: 1,
      id: `AmGraph-${idx}`,
      title: t,
      type: 'column',
      valueField: t,
    }));

const fetchUserProfile = data =>
  new Promise((resolve, reject) => {
    const userValue = sortUserValue(data.user_value);
    const userTableData = [];

    userTableData.push(['#', "User's fullname", 'Total Value, $']);

    const uids = userValue.map(v => v[0]);
    fetchAPI({
      endpoint: '/users/profile/_bulk',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids: uids }),
    })
      .then(response => {
        if (response.status !== 200) {
          throw new Error('fetching failed');
        }
        return response.json();
      })
      .then(json => {
        let formTotalAmount = 0;

        userValue
          .filter(value => json.profiles[value[0]])
          .map((value, index) => {
            formTotalAmount += value[1];
            userTableData.push([
              index + 1,
              json.profiles[value[0]].full_name,
              value[1].toFixed().replace(/(\d)(?=(\d{3})+$)/g, '$1,'),
            ]);
            return userTableData;
          });
        resolve({ userTableData, formTotalAmount });
      })
      .catch(err => reject(err));
  });

export const fetchFormStats = () => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: '/users/form_message_stats?all=1',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleReponse(res, dispatch))
      .then(json => {
        const formValueData = getChartData(json);
        const formGraphs = getChartLabel(json);
        const formTableData = getFormTableData(json);
        fetchUserProfile(json).then(userTableAndTotalData =>
          resolve(
            Object.assign(userTableAndTotalData, {
              formValueData,
              formGraphs,
              formTableData,
            })
          )
        );
      })
      .catch(err => reject(err));
  });

const getUserData = json => {
  const data = json.data;
  const initValue = 188; // as of Jan 2016

  const percentageOfActiveUser = [];
  const dailyGrowthRate = [];
  const cumulativeGrowthRate = [];

  data.sort((a, b) => new Date(a.key) - new Date(b.key));
  let prev = data[0];
  for (let i = 0; i < data.length; i++) {
    const curr = data[i];
    const active = curr.active_user_count;
    const total = curr.total_user_count;
    const date = new Date(curr.key);
    percentageOfActiveUser.push({
      value: ((active / total) * 100).toFixed(2),
      key: date,
    });
    cumulativeGrowthRate.push({
      value: (((total - initValue) / initValue) * 100).toFixed(2),
      key: date,
    });
    if (i !== 0) {
      dailyGrowthRate.push({
        value: (
          ((total - prev.total_user_count) / prev.total_user_count) *
          100
        ).toFixed(2),
        key: date,
      });
    }
    prev = curr;
  }

  const thisWeekActiveRate = (
    (json.weekly_active_users / data[data.length - 1].total_user_count) *
    100
  ).toFixed(2);
  return {
    dailyGrowthRate,
    percentageOfActiveUser,
    cumulativeGrowthRate,
    thisWeekActiveRate,
  };
};

export const fetchUsersStats = () => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - 30 * 24 * 3600000);

    fetchAPI({
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
      endpoint:
        '/stats/active_users_by_day' +
        `?from_date=${fromDate
          .toJSON()
          .slice(0, 10)}&to_date=${toDate.toJSON().slice(0, 10)}`,
    })
      .then(res => handleReponse(res, dispatch))
      .then(json => resolve(getUserData(json)))
      .catch(err => reject(err));
  });

const convertPageTitleToString = s => {
  const map = {
    'chat-page': 'Chat Page',
    'data-page': 'Data Page',
    'chat-channel': 'Official Channel Chat',
    'landing-page': 'Landing Page',
  };

  if (Object.keys(map).indexOf(s) >= 0) return map[s];
  if (s.startsWith('chat-group/')) return 'Group Chat';
  if (s.startsWith('chat-page/')) return 'P2P Chat';
  if (s.startsWith('data-page/')) return `Search ${s.substr(10)} (Data Page)`;
  if (s.startsWith('landing-page/')) {
    {
      return `Search ${s.substr(13)} (Landing Page)`;
    }

    return s;
  }
};

export const formatDuration = s => {
  if (s === 0) return '0';
  if (s >= 60) {
    const m = Math.round(s / 60);
    if (m >= 60) {
      return `${Math.round(m / 60)}h ${m % 60}m ${s % 60}s`;
    }
    return `${m}m ${s % 60}s`;
  }
  return `${s || 0}s`;
};

const processOverallPageViewCount = data => {
  // json.all.timestamps
  const tmpArray = data.sort(
    (a, b) => moment(a.timestamp) - moment(b.timestamp)
  ); // ascending
  const graphs = [];
  const pageviewData = tmpArray.map(item =>
    Object.assign(
      {},
      { timestamp: item.timestamp },
      ...item.details.map(detail => {
        const pageTitle = convertPageTitleToString(detail.pageTitle);
        if (graphs.indexOf(pageTitle) === -1) {
          graphs.push(pageTitle);
        }
        return {
          [pageTitle]: detail.totalVisit,
        };
      })
    )
  );

  const pageviewGraphs = graphs.map(e => ({
    balloonText: '[[title]]:<b>[[value]]</b>',
    fillAlphas: 1,
    id: `AmGraph-${e}`,
    title: e,
    type: 'column',
    valueField: e,
  }));

  return { pageviewData, pageviewGraphs };
};

const processOverallPageViewDuration = durationArr => {
  // durationArr: json.all.actions
  const duration = {};
  for (let i = 0; i < durationArr.length; i++) {
    const item = durationArr[i];
    const pageTitle = convertPageTitleToString(item.pageTitle);
    if (duration[pageTitle]) {
      duration[pageTitle] += item.totalTimeSpent;
    } else {
      duration[pageTitle] = item.totalTimeSpent;
    }
  }
  const durationData = Object.keys(duration)
    .sort()
    .map(e => ({ page: e, value: duration[e] }));

  return { durationData };
};

const processPVPerPage = data => {
  // data: array, per user json.individual
  const countTable = {};
  const durationTable = {};
  const headers = [];

  for (let i = 0; i < data.length; i++) {
    // loop through each user
    const u = data[i];
    const name = u.fullName;

    const durationData = u.actions;
    countTable[name] = {};
    durationTable[name] = {};
    for (let j = 0; j < durationData.length; j++) {
      const a = durationData[j];
      const pageTitle = convertPageTitleToString(a.pageTitle);
      if (headers.indexOf(pageTitle) === -1) headers.push(pageTitle);
      if (countTable[name][pageTitle]) {
        countTable[name][pageTitle] += a.totalVisit;
      } else {
        countTable[name][pageTitle] = a.totalVisit;
      }
      if (durationTable[name][pageTitle]) {
        durationTable[name][pageTitle] += a.totalTimeSpent;
      } else {
        durationTable[name][pageTitle] = a.totalTimeSpent;
      }
    }
  }

  headers.sort();
  const pageViewTableData = [
    ['User', ...headers],
    ...Object.keys(countTable)
      .sort()
      .map(e => [e, ...headers.map(page => countTable[e][page] || 0)]),
  ];
  const durationTableData = [
    ['User', ...headers],
    ...Object.keys(durationTable)
      .sort()
      .map(e => [e, ...headers.map(page => durationTable[e][page] || 0)]),
  ];

  return {
    pageViewTableData,
    durationTableData,
    pageViewTableDataPages: headers,
  };
};

const processPVPerUser = data => {
  // data: array, per user json.individual
  const pageviewCountPerUser = {};

  for (let i = 0; i < data.length; i++) {
    // loop through each user
    const u = data[i];
    const name = u.fullName;
    const pages = [];
    const PVData = u.timestamps
      .sort((a, b) => moment(b.timestamp) - moment(a.timestamp)) // sort by descending date
      .map(item => ({
        time: item.timestamp,
        data: Object.assign(
          // { pageTitle: pageviewCount }
          {},
          ...item.details.map(detail => {
            const pageTitle = convertPageTitleToString(detail.pageTitle);
            if (pages.indexOf(pageTitle) === -1) pages.push(pageTitle);
            return {
              [pageTitle]: detail.totalVisit,
            };
          })
        ),
      }));
    pages.sort(); // sort the pages alphabetically

    pageviewCountPerUser[name] = [
      ['Time', 'Total', ...pages],
      ...PVData.map(obj => [
        obj.time,
        pages
          .map(page => obj.data[page] || 0)
          .reduce((prev, curr) => prev + curr),
        ...pages.map(page => obj.data[page] || 0),
      ]), // convert to 2D array
    ];
  }

  return {
    pageviewCountPerUser,
  };
};

const getPromise = (
  fromDate,
  toDate,
  token,
  dispatch,
  individual = false,
  excludeFields
) => {
  let endpoint = `/analytic/page-view?from_date=${fromDate}&to_date=${toDate}`;
  if (individual) {
    endpoint += `&segment=individual&exclude_fields=${excludeFields}`;
  }

  return fetchAPI({
    endpoint,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then(res => handleReponse(res, dispatch));
};

const getDateStr = (end, offset) =>
  moment(end)
    .subtract(offset, 'days')
    .utc()
    .format('YYYY-MM-DD');

export const getOverallPageView = (fromDate, toDate) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    const datePairs = [
      [getDateStr(toDate, 6), toDate],
      [getDateStr(toDate, 13), getDateStr(toDate, 7)],
      [getDateStr(toDate, 20), getDateStr(toDate, 14)],
      [getDateStr(toDate, 27), getDateStr(toDate, 21)],
      [fromDate, getDateStr(toDate, 28)],
    ];
    Promise.all(
      datePairs.map(pair => getPromise(pair[0], pair[1], token, dispatch))
    ).then(jsonArr =>
      resolve(
        Object.assign(
          {},
          processOverallPageViewCount(
            jsonArr
              .map(json => json.all.timestamps)
              .reduce((prev, next) => prev.concat(next))
          ),
          processOverallPageViewDuration(
            jsonArr
              .map(json => json.all.actions)
              .reduce((prev, next) => prev.concat(next))
          )
        )
      )
    );
  });

export const getPVPerPage = (fromDate, toDate) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    getPromise(
      fromDate,
      toDate,
      token,
      dispatch,
      true,
      'totalTimeSpent,totalVisit,timestamps'
    ).then(json => resolve(processPVPerPage(json.individual)));
  });

export const getPVPerUser = (fromDate, toDate) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    getPromise(
      fromDate,
      toDate,
      token,
      dispatch,
      true,
      'totalTimeSpent,totalVisit,actions'
    ).then(json => {
      resolve(processPVPerUser(json.individual));
    });
  });

export const getNumberOfCompanies = () => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: '/analytic?name=internal-dashboard',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleReponse(res, dispatch))
      .then(json => resolve(json.company));
  });



// WEBPACK FOOTER //
// ./app/actions/internal/StatsActionCreators.js